import React, { useMemo, useState } from 'react';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { Spin } from 'antd';

import { inspectionApi } from '../features/inspections/inspectionAPI';
import { getDamageHistoryEntries, isDamageOnImage } from '../features/inspections/utils/annotationHistoryUtils';

interface Props {
  inspectionId: string;
  imageData: any;
  onClose?: () => void;
}

type HistoryRow = {
  username: string;
  action: string;
  type: string;
};

const EMPTY_DAMAGE_CODE = '00000';

const getHistoryDamageCode = (historyItem: any): string =>
  (historyItem?.aiag?.damageArea?.code || '00') +
  (historyItem?.aiag?.damageType?.code || '00') +
  (historyItem?.aiag?.damageSeverity?.code || '0');

const isEmptyDamageCode = (code?: string | null): boolean => !code || code === EMPTY_DAMAGE_CODE;

/** Deleted history rows often reset AIAG codes to 00000 — reuse the prior real code. */
const resolveHistoryDamageCode = (history: any[], index: number): string => {
  const currentCode = getHistoryDamageCode(history[index]);
  const status = String(history[index]?.status ?? '').toLowerCase();
  const isDeleted = status === 'deleted' || String(history[index]?.action ?? '').toLowerCase() === 'deleted';

  if (!isDeleted || !isEmptyDamageCode(currentCode)) {
    return currentCode;
  }

  // History is typically oldest → newest; walk backward for the last meaningful code.
  for (let previousIndex = index - 1; previousIndex >= 0; previousIndex -= 1) {
    const previousCode = getHistoryDamageCode(history[previousIndex]);
    if (!isEmptyDamageCode(previousCode)) {
      return previousCode;
    }
  }

  // Fallback if the API returns newest → oldest.
  for (let nextIndex = index + 1; nextIndex < history.length; nextIndex += 1) {
    const nextCode = getHistoryDamageCode(history[nextIndex]);
    if (!isEmptyDamageCode(nextCode)) {
      return nextCode;
    }
  }

  return currentCode;
};

const formatHistoryAction = (historyItem: any): string => {
  const status = String(historyItem?.status ?? '').toLowerCase();
  const rawAction = String(historyItem?.action ?? '').toLowerCase();

  // A deleted history entry should always read "Deleted", regardless
  // of what the original action (created/updated) was.
  if (status === 'deleted' || rawAction === 'deleted') {
    return 'Deleted';
  }

  if (rawAction.includes('gsip') || status.includes('gsip')) {
    return 'GSIP Verified';
  }

  if (rawAction.includes('approv') || status.includes('approv')) {
    return 'Approved';
  }

  if (!rawAction) {
    return '';
  }

  return rawAction.charAt(0).toUpperCase() + rawAction.slice(1).toLowerCase();
};

const historyHasActionMatching = (history: any[], matcher: (value: string) => boolean): boolean =>
  history.some(historyItem => {
    const status = String(historyItem?.status ?? '').toLowerCase();
    const action = String(historyItem?.action ?? '').toLowerCase();
    return matcher(action) || matcher(status);
  });

const getDamageReviewStatus = (damage: any): string =>
  String(damage?.review_status ?? damage?.reviewStatus ?? damage?.status ?? '').toUpperCase();

const AnnotationHistory: React.FC<Props> = ({ inspectionId, imageData, onClose }) => {
  const [searchText, setSearchText] = useState('');
  const {
    data: damages = [],
    isLoading,
    isFetching,
  } = inspectionApi.useGetDamagesQuery(inspectionId, {
    skip: !inspectionId,
  });

  const rowData = useMemo(() => {
    const rows: HistoryRow[] = [];
    damages
      .filter((damage: any) => isDamageOnImage(damage, imageData))
      .forEach((damage: any) => {
        const history = getDamageHistoryEntries(damage);

        history.forEach((historyItem: any, historyIndex: number) => {
          rows.push({
            // A history entry can still carry no user — one written before the
            // damage tables tracked an author, or by a since-removed account. The
            // typed row promises a string, and the search filter below calls
            // .toLowerCase() on it, so a null here crashed the panel on the first
            // keystroke in the search box.
            username: historyItem.changedByName ?? '',
            action: formatHistoryAction(historyItem),
            type: resolveHistoryDamageCode(history, historyIndex),
          });
        });

        const lastHistoryItem = history[history.length - 1] as { changedByName?: string } | undefined;
        const fallbackType = history.length > 0 ? resolveHistoryDamageCode(history, history.length - 1) : EMPTY_DAMAGE_CODE;
        const fallbackUsername = lastHistoryItem?.changedByName ?? '';
        const reviewStatus = getDamageReviewStatus(damage);

        // Auto-approve after manual create updates review_status, but some APIs
        // never append matching history rows. Surface Approved / GSIP Verified
        // from the damage status when history only has create/update.
        const isApproved = ['APPROVED', 'APPROVE'].includes(reviewStatus);
        if (isApproved && !historyHasActionMatching(history, value => value.includes('approv'))) {
          rows.push({
            username: fallbackUsername,
            action: 'Approved',
            type: fallbackType,
          });
        }

        const isGsipVerified = reviewStatus.includes('GSIP');
        if (isGsipVerified && !historyHasActionMatching(history, value => value.includes('gsip'))) {
          rows.push({
            username: fallbackUsername,
            action: 'GSIP Verified',
            type: fallbackType,
          });
        }
      });

    return rows;
  }, [damages, imageData]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return rowData;
    }

    return rowData.filter(row => [row.username, row.action, row.type].some(value => value.toLowerCase().includes(normalizedSearch)));
  }, [rowData, searchText]);

  return (
    <div className="flex h-full min-w-0 flex-col px-1 py-0.5 text-[13px]">
      <div className="mx-auto mb-2 flex w-[92%] items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <SearchOutlined className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-300" />
          <input
            type="text"
            placeholder="Search by code"
            value={searchText}
            onChange={event => setSearchText(event.target.value)}
            className="h-8 w-full rounded-md border border-slate-200 bg-white pl-8 pr-2 text-[11px] text-slate-600 outline-none placeholder:text-slate-300"
          />
        </div>
        {onClose && (
          <button
            type="button"
            aria-label="Close history"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black/45 text-[11px] text-white transition hover:bg-black/60">
            <CloseOutlined />
          </button>
        )}
      </div>

      <div className="mx-auto flex min-h-0 w-[92%] flex-1 overflow-x-auto">
        {isLoading || isFetching ? (
          <div className="flex h-full min-h-[220px] w-full items-center justify-center rounded-md border border-slate-100 bg-slate-50/70">
            <Spin size="large" tip="Loading history..." />
          </div>
        ) : (
          <table className="w-full border-collapse self-start text-left">
            <thead className="border-y border-[#d9d9d9]">
              <tr>
                <th className="px-2 py-2 text-[11px] font-semibold text-[#333]">User name</th>
                <th className="px-2 py-2 text-[11px] font-semibold text-[#333]">Action</th>
                <th className="px-2 py-2 text-[11px] font-semibold text-[#333]">Code</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, index) => (
                <tr key={`${row.username}-${row.action}-${row.type}-${index}`}>
                  <td className="px-2 py-2 text-[11px] text-[#333]">{row.username}</td>
                  <td className="px-2 py-2 text-[11px] text-[#333]">{row.action}</td>
                  <td className="px-2 py-2 text-[11px] text-[#333]">{row.type}</td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-2 py-3 text-center text-[#777]">
                    No history found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AnnotationHistory;
